// Prices approved by the owner 2026-10-31 for publication: small pet figures
// from studio quotes (3–4 inch, $300 painted, $150 single color, July 2026) and
// large realistic sculptures from a 2025 studio quote ($600 model; 20, 35 and
// ~47 inch). No real or flocked fur: surfaces are sculpted and painted. Sources
// and limits are in .seo/truth.md "Pet figurines".
const { figurePricing } = require('./custom-figurines')

const petPricing = {
  smallSize: '3–4 inch',
  smallPainted: 300,
  smallSingleColor: 150,
  largeModeling: 600,
  large: [
    { size: '20 inches', price: '$3,000' },
    { size: '35 inches', price: '$5,000' },
    { size: 'About 4 feet', price: '$15,000–$20,000' },
  ],
  smallWeeks: figurePricing.prototypeWeeks,
}

const petFigurines = {
  path: '/services/custom-figurines/pets/',
  reviewedDate: '2026-10-31',
  reviewedDateLabel: 'October 31, 2026',
  title: 'Custom Pet Figurines & Dog Statues from Photos | PrintAWorld',
  description: `Custom pet figurines and dog or cat statues, sculpted from your photos and hand-painted in New York. ${petPricing.smallSize} figures from $${petPricing.smallPainted}.`,
  answer: `PrintAWorld sculpts custom pet figurines and statues in New York from your photos. We model your dog, cat or other pet in 3D, send the model for you to approve, then print it in resin and paint it by hand to match their coat and markings. A ${petPricing.smallSize} painted figure starts at $${petPricing.smallPainted} ($${petPricing.smallSingleColor} in a single color), and realistic sculptures start at ${petPricing.large[0].price} at ${petPricing.large[0].size}. We ship anywhere in the U.S.`,
}

const sizes = [
  {
    name: 'Desk figure',
    size: petPricing.smallSize,
    price: `From $${petPricing.smallPainted}`,
    detail: `Hand-painted to match your pet’s coat and markings. In a single color, from $${petPricing.smallSingleColor}.`,
  },
  {
    name: 'Realistic sculpture',
    size: petPricing.large[0].size,
    price: `${petPricing.large[0].price}`,
    detail: `A lifelike pose with sculpted fur and painted color. Plus a one-time $${petPricing.largeModeling} 3D model.`,
  },
  {
    name: 'Statement sculpture',
    size: petPricing.large[1].size,
    price: `${petPricing.large[1].price}`,
    detail: `For a home, shop or café centerpiece. Same $${petPricing.largeModeling} model.`,
  },
  {
    name: 'Near life-size',
    size: petPricing.large[2].size,
    price: petPricing.large[2].price,
    detail: 'Quoted from photos and the space it will stand in.',
  },
]

const occasions = [
  {
    title: 'A memorial',
    body: 'A keepsake of a pet you’ve lost, made from the photos you already have. Tell us what you want to remember: the head tilt, the favorite toy, the way they slept.',
    link: ['More pet memorial ideas', '/guides/pet-memorial-ideas/'],
  },
  {
    title: 'A gift',
    body: 'A desk-sized portrait of someone’s dog or cat for a birthday or the holidays. Order early; the 3D model needs your approval before we print.',
  },
  {
    title: 'A cartoon version',
    body: 'A simplified, chibi or cartoon style still looks like your pet and costs less to sculpt and paint than a realistic one.',
  },
  {
    title: 'A shop or café mascot',
    body: 'The resident cat or dog as a sculpture for the counter, a window or a photo spot.',
  },
  {
    title: 'For pet brands',
    body: 'Figures made from your customers’ pet photos, or your brand’s own animal character. We quote per piece; resin runs go up to 100 and molded vinyl starts at 300.',
  },
  {
    title: 'Copying a statue you own',
    body: 'We can 3D scan an existing pet statue so it can be repaired, resized or reproduced.',
    link: ['3D scanning', '/services/3d-scanning/'],
  },
]

const photoTips = [
  'The face from the front at your pet’s eye level, in daylight. Phone photos are fine; blurry or dark ones aren’t.',
  'A side view of the head, so we get the muzzle, ears and profile right.',
  'The whole body standing, from the side, for proportions and markings.',
  'Close-ups of anything that makes them them: a split ear, a white sock, a collar or tag.',
  'The pose you want, even from a different photo.',
]

const faqs = [
  {
    question: 'How much does a custom pet figurine cost?',
    answer: `A ${petPricing.smallSize} pet figurine starts at $${petPricing.smallPainted} hand-painted, or $${petPricing.smallSingleColor} in a single color. Realistic sculptures start at ${petPricing.large[0].price} at ${petPricing.large[0].size} and ${petPricing.large[1].price} at ${petPricing.large[1].size}, plus a one-time $${petPricing.largeModeling} 3D model; near life-size pieces around 4 feet run ${petPricing.large[2].price}. Pose, coat and markings move the price, and your written quote confirms it before we start.`,
  },
  {
    question: 'Can you make a figurine from photos of my pet?',
    answer:
      'Yes. Send the face from the front and side and the whole body standing. We model your pet in 3D and send you the model to approve before anything is printed.',
  },
  {
    question: 'Can you make a figure of a pet that has passed away?',
    answer:
      'Yes. Send the photos you have. If some angles are missing, we work from what you send and show you the 3D model, so you can tell us what to change before it’s printed and painted.',
  },
  {
    question: 'Will it have real fur?',
    answer:
      'No. We sculpt the fur as texture in the model and paint it to match your pet’s colors and markings. We don’t apply real or flocked fur.',
  },
  {
    question: 'Can you add my pet’s name?',
    answer:
      'Yes. Tell us the name and we put it on the base. Your quote confirms how it’s added.',
  },
  {
    question: 'How long does a pet figurine take?',
    answer: `A small figure typically takes ${petPricing.smallWeeks}, including the 3D model and your approval. Large sculptures take longer. Tell us your date and we’ll tell you what fits.`,
  },
  {
    question: 'Do you make cat and other pet figurines, or only dogs?',
    answer:
      'Dogs, cats and other pets you have good photos of. Very fine details like whiskers are sculpted thicker or painted on so the figure stays sturdy.',
  },
  {
    question: 'Why do some sites sell pet figurines for under $100?',
    answer: `Online pet-figurine shops can sell for under $100 by working from templates or automated 3D models, and that works for a quick gift. Ours are modeled for your pet by a person, approved by you in 3D, and painted by hand, which is why a painted figure starts at $${petPricing.smallPainted}.`,
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States. Pickup from our New York studio is available by appointment.',
  },
]

module.exports = { petFigurines, petPricing, sizes, occasions, photoTips, faqs }
